import type { RecipeFilters } from '@/api/types'

export const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'rating', label: 'Rating' },
  { value: 'effort', label: 'Effort' },
  { value: 'prep_count', label: 'Times made' },
  { value: 'last_prep', label: 'Last made' },
  { value: 'cuisine', label: 'Cuisine' },
  { value: 'protein_base', label: 'Protein base' },
  { value: 'calories', label: 'Calories per serving' },
  { value: 'protein_per_serving', label: 'Protein per serving' },
  { value: 'carbs_per_serving', label: 'Carbs per serving' },
  { value: 'fat_per_serving', label: 'Fat per serving' },
  { value: 'fiber_per_serving', label: 'Fibre per serving' },
  { value: 'ingredient_count', label: 'Ingredient count' },
]

export interface ParsedRecipeSearch {
  filters: RecipeFilters
  q: string
  sort: string
  order: 'asc' | 'desc'
  vegetarian: 'all' | 'yes' | 'no'
  excluded: 'all' | 'yes' | 'no'
  view: 'grid' | 'table'
}

/**
 * Filter state lives in the query string, so a filtered view can be bookmarked,
 * shared, and returned to with the browser's back button.
 */
export function parseSearchParams(params: URLSearchParams): ParsedRecipeSearch {
  const list = (key: string): string[] =>
    params.getAll(key).filter((value) => value !== '')

  const orderParam = params.get('order')
  const vegParam = params.get('vegetarian')
  const excludedParam = params.get('excluded')
  const minRating = Number(params.get('min_rating'))
  const maxEffort = Number(params.get('max_effort'))
  const notPrepped = Number(params.get('not_prepped_within_days'))
  const viewParam = params.get('view')

  const filters: RecipeFilters = {
    q: params.get('q') ?? undefined,
    cuisine: list('cuisine'),
    protein_base: list('protein_base'),
    tags: list('tags'),
    vegetarian: vegParam === 'yes' ? true : vegParam === 'no' ? false : undefined,
    excluded: excludedParam === 'yes' ? true : excludedParam === 'no' ? false : undefined,
    min_rating: Number.isFinite(minRating) && minRating > 0 ? minRating : undefined,
    max_effort: Number.isFinite(maxEffort) && maxEffort > 0 ? maxEffort : undefined,
    not_prepped_within_days:
      Number.isFinite(notPrepped) && notPrepped > 0 ? notPrepped : undefined,
  }

  return {
    filters,
    q: params.get('q') ?? '',
    sort: params.get('sort') ?? 'name',
    order: orderParam === 'desc' ? 'desc' : 'asc',
    vegetarian: vegParam === 'yes' ? 'yes' : vegParam === 'no' ? 'no' : 'all',
    excluded: excludedParam === 'yes' ? 'yes' : excludedParam === 'no' ? 'no' : 'all',
    view: viewParam === 'table' ? 'table' : 'grid',
  }
}

export function buildSearchParams(state: {
  q: string
  cuisine: string[]
  protein_base: string[]
  tags: string[]
  vegetarian: 'all' | 'yes' | 'no'
  excluded: 'all' | 'yes' | 'no'
  min_rating: number | null
  max_effort: number | null
  not_prepped_within_days: number | null
  sort: string
  order: 'asc' | 'desc'
  view: 'grid' | 'table'
}): URLSearchParams {
  const params = new URLSearchParams()
  // Trimmed for the emptiness test only. The search input is controlled by this
  // value, so storing a trimmed one would eat the space the user just typed.
  if (state.q.trim()) params.set('q', state.q)
  for (const key of ['cuisine', 'protein_base', 'tags'] as const) {
    for (const value of state[key]) params.append(key, value)
  }
  if (state.vegetarian !== 'all') params.set('vegetarian', state.vegetarian)
  if (state.excluded !== 'all') params.set('excluded', state.excluded)
  if (state.min_rating) params.set('min_rating', String(state.min_rating))
  if (state.max_effort) params.set('max_effort', String(state.max_effort))
  if (state.not_prepped_within_days) {
    params.set('not_prepped_within_days', String(state.not_prepped_within_days))
  }
  if (state.sort !== 'name') params.set('sort', state.sort)
  if (state.order !== 'asc') params.set('order', state.order)
  if (state.view !== 'grid') params.set('view', state.view)
  return params
}